import {
  combineLatestTyped,
  filterNotUndefined,
  IList,
  IMap
} from 'annotation-tools/utils';
import { Observable, ReplaySubject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  groupBy,
  map,
  mergeMap,
  scan,
  shareReplay
} from 'rxjs/operators';
import { FetchErrorType } from '../../../api/types/common/fetch-error-type';
import { FetchResultType } from '../../../api/types/common/fetch-result';
import { token$ } from '../../auth-state';
import {
  ListWithPromiseState,
  listWithPromiseStateEq,
  listWithPromiseStateInit
} from './list-with-status';
import { attachPromiseStateToList } from './rxjs-operator/attach-promise-state-to-list';

/**
 * @description
 * refetch関数の実行時に発火させるためのstreamを生成
 */
export const fetchIdStreamGenerator = () => new ReplaySubject<number>(1);

/**
 * @description
 * refetch関数を生成
 */
export const fetchListFnGenerator = (fetchSignal$: ReplaySubject<number>) => (
  id: number
) => {
  fetchSignal$.next(id);
};

type OneIdListReducerState<T> = {
  id: number;
  list: ListWithPromiseState<T>;
};

/**
 * @description
 * fetchListOf(id)を実行すると
 * allLists$のidの要素を更新する
 */
export const updateStreamGenerator = <T>(
  fetchSignal$: ReplaySubject<number>,
  fetchFn: (
    token: string
  ) => (id: number) => Promise<FetchResultType<IList<T>, FetchErrorType<void>>>
): Observable<OneIdListReducerState<T>> =>
  combineLatestTyped(
    token$.pipe(filterNotUndefined()),
    fetchSignal$.asObservable()
  ).pipe(
    debounceTime(100),  // Avoid performance overhead caused by duplicate API requests
    groupBy(([_, id]) => id),
    mergeMap(group =>
      group.pipe(
        map(([token, id]) => fetchFn(token)(id)),
        attachPromiseStateToList('mergeMap'),
        map(list => ({ id: group.key, list }))
      )
    ),
    shareReplay(1)
  );

export const oneIdListSetReducerGenerator = <T>() => (
  acc: IMap<number, ListWithPromiseState<T>>,
  { id, list }: OneIdListReducerState<T>
) => acc.set(id, list);

/**
 * @description
 * すべてのlistの集合．必要になったものからfetchして蓄える．
 * 複数のcomponentで共有するため．
 */
export const oneIdListSetStreamGenerator = <T>(
  update$: Observable<OneIdListReducerState<T>>,
  allListsReducer: (
    acc: IMap<number, ListWithPromiseState<T>>,
    update: OneIdListReducerState<T>
  ) => IMap<number, ListWithPromiseState<T>>
): Observable<IMap<number, ListWithPromiseState<T>>> =>
  update$.pipe(
    scan(allListsReducer, IMap<number, ListWithPromiseState<T>>()),
    shareReplay(1)
  );

export const listOfIdFnGenerator = <T>(
  allLists$: Observable<IMap<number, ListWithPromiseState<T>>>
) => (id: number): Observable<ListWithPromiseState<T>> =>
  allLists$.pipe(
    map(list => list.get(id) ?? listWithPromiseStateInit<T>()),
    distinctUntilChanged(listWithPromiseStateEq),
    shareReplay(1)
  );
